import type { DashboardRoute } from "#/types/navigation";
import { Badge, Button } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Props {
  route: DashboardRoute;
  onClick: () => void;
  /** Open counts by full path (e.g. "/data-health"). Shown as a count on the entry, a dot on a collapsed parent. */
  badges?: Record<string, number>;
}

/** A count next to a navigation entry, or nothing when there is nothing open. */
const NavCount = ({ count }: { count: number | undefined }) =>
  count && count > 0 ? (
    <Badge size="sm" circle color="red" className="shrink-0 tabular-nums">
      {count > 99 ? "99+" : count}
    </Badge>
  ) : null;

const SideNavButton = ({ route, onClick, badges }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState<boolean>(false);

  useEffect(() => {
    if (route?.children && route?.children?.length > 0 && location.pathname.includes(route.href)) {
      setOpened(true);
    } else {
      setOpened(false);
    }
  }, [location.pathname, route]);

  if (route.children && route.children.length > 0) {
    const childrenOpen = route.children.reduce(
      (sum, item) => sum + (badges?.[`${route.href}${item.href}`] ?? 0),
      0
    );
    return (
      <div
        className={`flex w-full flex-col transition-all duration-100 ${opened && "rounded-lg bg-white/40"}`}
      >
        <Button
          onClick={() => {
            setOpened((prev) => !prev);
          }}
          fullWidth
          classNames={{
            root: `flex justify-between items-center0  px-4 h-auto min-h-12 transition-all hover:bg-client-color/10 whitespace-normal ${
              opened ? "bg-client-color/40 border-b border-b-white" : "bg-transparent "
            }`,
            label: `text-left break-words`,
          }}
          leftSection={
            <div
              className={`flex-shrink-0 transition-all duration-300 ${opened ? "rotate-0" : "-rotate-90"}`}
            >
              <IconChevronDown size={20} stroke={2.0} />
            </div>
          }
          rightSection={
            !opened && childrenOpen > 0 ? (
              <span
                aria-label={String(childrenOpen)}
                className="h-2 w-2 shrink-0 rounded-full bg-red-500"
              />
            ) : undefined
          }
        >
          {t(route.label)}
        </Button>

        {opened && (
          <div className="flex w-full flex-col gap-2 px-2 py-2">
            {route.children.map((item) => {
              const fullPath = `${route.href}${item.href}`;
              const active = location.pathname.includes(fullPath);
              return (
                <Button
                  key={item.href}
                  size="xs"
                  onClick={() => {
                    void navigate(fullPath);
                    onClick();
                  }}
                  fullWidth
                  autoContrast
                  classNames={{
                    root: `flex justify-between items-center0  px-4 h-auto min-h-10 rounded-full transition-colors whitespace-normal ${
                      active
                        ? "bg-white text-black hover:bg-client-color/30"
                        : "bg-transparent hover:bg-client-color/30 text-black"
                    }`,
                    label: `text-left break-words`,
                  }}
                  leftSection={<item.icon size={20} stroke={2.0} />}
                  rightSection={<NavCount count={badges?.[fullPath]} />}
                >
                  {t(item.label)}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Button
      onClick={() => {
        void navigate(route.href);
        onClick();
      }}
      fullWidth
      classNames={{
        root: `flex justify-between items-center0  px-4 h-auto min-h-12 rounded-full transition-colors whitespace-normal ${
          location.pathname === route.href
            ? "bg-white text-black hover:bg-white hover:text-black"
            : "bg-transparent hover:bg-white/20"
        }`,
        label: `text-left break-words`,
      }}
      leftSection={<route.icon size={20} stroke={2.0} />}
      rightSection={<NavCount count={badges?.[route.href]} />}
    >
      {t(route.label)}
    </Button>
  );
};

export default SideNavButton;
